// Extracted from AIWorkoutGenerator.js during Phase 4 refactor (sprint 2026-06-20).
import React, { useState, useEffect } from 'react';
import { api } from '../../context/AppContext';
import useExerciseHistory from '../../hooks/useExerciseHistory';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Zap, Loader2, Play, Dumbbell, Clock, Repeat, Timer, ChevronDown, ChevronUp,
  Battery, BatteryLow, BatteryFull, Star, ThumbsUp, Send, Info,
  AlertTriangle, Shield, Save, BookmarkCheck, ExternalLink, History, RotateCcw, Trophy, Crosshair,
  CalendarDays, Layers, Target, Wrench, HeartPulse, BookOpen, Flame, TrendingUp
} from 'lucide-react';
import { toast } from 'sonner';

export default function DraftPlanPreview({ plan, onMutate, language }) {
  const fr = language === 'fr';
  const weeks = Array.isArray(plan?.weeks) ? plan.weeks : [];
  const [weekIdx, setWeekIdx] = useState(0);
  const [openDay, setOpenDay] = useState(0);

  if (!weeks.length) {
    return (
      <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30 text-xs text-amber-200" data-testid="draft-empty">
        {fr
          ? "Aucune séance générée. Réessaie ou ajuste les paramètres."
          : 'No session was generated. Try again or tweak the parameters.'}
      </div>
    );
  }

  const days = Array.isArray(weeks[weekIdx]?.days) ? weeks[weekIdx].days : [];

  // Mutator helpers — operate on the parent `plan` object so the eventual
  // /deploy call ships the user-edited draft.
  const removeExercise = (di, section, ei) => {
    const next = JSON.parse(JSON.stringify(plan));
    const day = next.weeks?.[weekIdx]?.days?.[di];
    if (!day || !Array.isArray(day[section])) return;
    day[section].splice(ei, 1);
    onMutate(next);
  };

  return (
    <div className="space-y-3" data-testid="draft-plan-preview">
      {/* Reassurance banner */}
      <div className="rounded-lg bg-amber-500/10 border border-amber-400/40 px-3 py-2 flex items-start gap-2" data-testid="draft-preview-hint">
        <Info className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
        <p className="text-[11px] text-amber-100/90 leading-snug">
          {fr
            ? <>Aperçu du plan — touche un jour pour le détailler, et le bouton <span className="font-bold">✕</span> pour retirer un exercice avant d&apos;activer.</>
            : <>Plan preview — tap a day to expand it, use <span className="font-bold">✕</span> to remove an exercise before activation.</>}
        </p>
      </div>

      {/* Week selector (only if multi-week) */}
      {weeks.length > 1 && (
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 -mx-1 px-1" data-testid="draft-weeks-rail">
          {weeks.map((w, i) => (
            <button
              key={i}
              type="button"
              onClick={() => { setWeekIdx(i); setOpenDay(0); }}
              data-testid={`draft-week-${i}`}
              className={`shrink-0 px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider transition-all ${
                i === weekIdx
                  ? 'bg-orange-500 text-white shadow-[0_0_12px_rgba(249,115,22,0.4)]'
                  : 'bg-secondary/40 text-muted-foreground hover:text-foreground hover:bg-secondary/60'
              }`}
            >
              {fr ? `Sem. ${w.week ?? i + 1}` : `Wk ${w.week ?? i + 1}`}
            </button>
          ))}
        </div>
      )}

      {/* Days of the selected week */}
      <div className="space-y-2">
        {days.map((day, di) => {
          const isOpen = openDay === di;
          const warmup = Array.isArray(day.warmup) ? day.warmup : [];
          const exos = Array.isArray(day.exercises) ? day.exercises : [];
          const cooldown = Array.isArray(day.cooldown) ? day.cooldown : [];
          const dayLabel = day.name || (fr ? `Jour ${day.day ?? di + 1}` : `Day ${day.day ?? di + 1}`);
          const totalEx = warmup.length + exos.length + cooldown.length;
          // Iter. 406.fr.30 — Count unique superset pairs in the main session
          // so the day header can show "X paires · intensité +35%".
          const pairKeys = new Set();
          for (const ex of exos) {
            const sg = ex?.superset_group;
            const m = typeof sg === 'string' && sg.match(/^(\d+)[A-Z]$/i);
            if (m) pairKeys.add(m[1]);
          }
          const pairCount = pairKeys.size;
          return (
            <div
              key={di}
              className="rounded-xl border border-orange-500/30 bg-gradient-to-br from-orange-500/5 to-transparent overflow-hidden"
              data-testid={`draft-day-${di}`}
            >
              <button
                type="button"
                onClick={() => setOpenDay(isOpen ? -1 : di)}
                className="w-full px-3 py-2.5 flex items-center justify-between gap-2 hover:bg-orange-500/10 transition-colors text-left"
                data-testid={`draft-day-${di}-toggle`}
              >
                <div className="flex items-center gap-2 min-w-0">
                  <CalendarDays className="w-4 h-4 text-orange-300 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-[9px] uppercase tracking-widest text-orange-300 font-bold leading-none">
                      {fr ? `Jour ${day.day ?? di + 1}` : `Day ${day.day ?? di + 1}`}
                    </p>
                    <p className="text-sm font-bold text-foreground truncate mt-0.5">{dayLabel}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {/* Iter. 406.fr.30 — Badge SS pairs */}
                  {pairCount > 0 && (
                    <span
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-500/20 border border-orange-400/50 text-orange-200 text-[9px] font-black uppercase tracking-wider shadow-[0_0_8px_rgba(249,115,22,0.35)]"
                      data-testid={`draft-day-${di}-ss-badge`}
                      title={fr
                        ? `${pairCount} paire(s) de supersets — densité accrue (~+35 % d'intensité)`
                        : `${pairCount} superset pair(s) — higher density (~+35% intensity)`}
                    >
                      <Layers className="w-2.5 h-2.5" />
                      {pairCount}× SS
                    </span>
                  )}
                  <span className="text-[10px] text-muted-foreground tabular-nums">
                    {totalEx} {fr ? 'mvts' : 'ex'}
                  </span>
                  {isOpen ? <ChevronUp className="w-4 h-4 text-orange-300" /> : <ChevronDown className="w-4 h-4 text-orange-300" />}
                </div>
              </button>

              {isOpen && (
                <div className="px-3 pb-3 space-y-3 border-t border-orange-500/20 pt-3">
                  {/* Iter. 406.fr.32 — Structure warning (e.g., < 4 exos) */}
                  {day._structure_warning && (
                    <div
                      className="flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-500/15 border border-amber-400/50"
                      data-testid={`draft-day-${di}-warning`}
                    >
                      <Info className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
                      <p className="text-[11px] text-amber-100/90 leading-snug">
                        {day._structure_warning}
                      </p>
                    </div>
                  )}
                  {warmup.length > 0 && (
                    <DraftSection
                      title={fr ? 'Échauffement' : 'Warm-up'}
                      tint="yellow"
                      Icon={Timer}
                      items={warmup}
                      onRemove={(ei) => removeExercise(di, 'warmup', ei)}
                      sectionKey="warmup"
                      di={di}
                      language={language}
                    />
                  )}
                  {exos.length > 0 ? (
                    <DraftSection
                      title={fr ? 'Corps de séance' : 'Main session'}
                      tint="orange"
                      Icon={Dumbbell}
                      items={exos}
                      onRemove={(ei) => removeExercise(di, 'exercises', ei)}
                      sectionKey="exercises"
                      di={di}
                      language={language}
                    />
                  ) : (
                    <p className="text-xs text-muted-foreground italic">
                      {fr ? 'Aucun exercice principal pour ce jour.' : 'No main exercise for this day.'}
                    </p>
                  )}
                  {cooldown.length > 0 && (
                    <DraftSection
                      title={fr ? 'Retour au calme' : 'Cool-down'}
                      tint="cyan"
                      Icon={Star}
                      items={cooldown}
                      onRemove={(ei) => removeExercise(di, 'cooldown', ei)}
                      sectionKey="cooldown"
                      di={di}
                      language={language}
                    />
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function DraftSection({ title, tint, Icon, items, onRemove, sectionKey, di, language }) {
  const fr = language !== 'en';
  const tintMap = {
    yellow: 'text-yellow-300',
    orange: 'text-orange-300',
    cyan: 'text-cyan-300',
  };

  // Iter. 406.fr.30/32 — Helpers Super Sets.
  // `superset_group` arrives as "1A", "1B", "2A", "2B"… The pair NUMBER is
  // everything before the trailing letter; the SIDE is the letter itself.
  const pairOf = (ex) => {
    const sg = ex?.superset_group;
    if (typeof sg !== 'string') return null;
    const m = sg.match(/^(\d+)([A-Z])$/i);
    return m ? { number: m[1], side: m[2].toUpperCase(), key: m[1] } : null;
  };

  // Iter. 406.fr.32 — Taxonomie superset_type (Yuri spec).
  const SUPERSET_TYPE_LABEL = {
    antagonist: { fr: 'Antagoniste', en: 'Antagonist', color: 'bg-rose-500/20 text-rose-200 border-rose-400/50' },
    agonist:    { fr: 'Agoniste',    en: 'Agonist',    color: 'bg-fuchsia-500/20 text-fuchsia-200 border-fuchsia-400/50' },
    upper_lower:{ fr: 'Haut/Bas',    en: 'Upper/Lower',color: 'bg-emerald-500/20 text-emerald-200 border-emerald-400/50' },
    pre_fatigue:{ fr: 'Pré-fatigue', en: 'Pre-fatigue',color: 'bg-amber-500/20 text-amber-200 border-amber-400/50' },
  };
  const typeLabel = (t) => {
    const cfg = SUPERSET_TYPE_LABEL[String(t || '').toLowerCase()];
    if (!cfg) return null;
    return { label: fr ? cfg.fr : cfg.en, color: cfg.color };
  };

  return (
    <div data-testid={`draft-section-${di}-${sectionKey}`}>
      <p className={`text-[10px] font-heading uppercase tracking-widest font-bold mb-1.5 flex items-center gap-1.5 ${tintMap[tint] || 'text-foreground'}`}>
        <Icon className="w-3 h-3" /> {title}
      </p>
      <ul className="space-y-1.5">
        {items.map((ex, ei) => {
          const pair = pairOf(ex);
          const prevPair = ei > 0 ? pairOf(items[ei - 1]) : null;
          const startsNewPair = sectionKey === 'exercises' && pair?.side === 'A' && pair.key !== prevPair?.key;
          return (
            <React.Fragment key={ei}>
              {startsNewPair && (
                <li
                  className="flex items-center gap-2 pt-1.5 pb-0.5 flex-wrap"
                  data-testid={`draft-superset-banner-${di}-${pair.key}`}
                  role="presentation"
                >
                  <Layers className="w-3 h-3 text-orange-400 shrink-0" />
                  <span className="text-[9px] font-black uppercase tracking-widest text-orange-300">
                    {fr ? `Super Set ${pair.number}` : `Super Set ${pair.number}`}
                  </span>
                  {/* Iter. 406.fr.32 — Taxonomy chip (antagonist/agonist/etc.) */}
                  {(() => {
                    const t = typeLabel(ex.superset_type);
                    return t ? (
                      <span
                        className={`px-1.5 py-0.5 rounded-full border text-[9px] font-bold uppercase tracking-wider ${t.color}`}
                        data-testid={`draft-superset-type-${di}-${pair.key}`}
                      >
                        {t.label}
                      </span>
                    ) : null;
                  })()}
                  {/* Iter. 406.fr.32 — Rest semantics : 0s entre A→B, full rest après B */}
                  <span className="text-[9px] text-orange-200/80 tabular-nums inline-flex items-center gap-1">
                    {pair.number}A
                    <span className="text-cyan-300 font-black">→</span>
                    {pair.number}B
                    <span className="text-emerald-300/70 mx-1">·</span>
                    <span className="text-cyan-200/80">{fr ? '0s entre' : '0s between'}</span>
                    {(() => {
                      // The "rest after the pair" is on the B exercise.
                      const nextEx = items[ei + 1];
                      const bRest = nextEx?.rest_seconds;
                      return bRest != null ? (
                        <>
                          <span className="text-emerald-300/70">·</span>
                          <span className="text-emerald-200/90 font-semibold">
                            {bRest}s {fr ? 'après' : 'after'}
                          </span>
                        </>
                      ) : null;
                    })()}
                  </span>
                  <div className="flex-1 h-px bg-orange-500/30 min-w-[12px]" />
                </li>
              )}
              <li
                className={`flex items-start gap-2 px-2.5 py-2 rounded-lg bg-secondary/30 border ${
                  pair ? 'border-orange-500/40 border-l-2 border-l-orange-400' : 'border-border/40'
                }`}
                data-testid={`draft-${sectionKey}-${di}-${ei}`}
                data-superset-group={pair ? `${pair.number}${pair.side}` : undefined}
              >
                <span className={`shrink-0 w-5 h-5 rounded-full text-[10px] font-black flex items-center justify-center mt-0.5 ${
                  pair ? 'bg-orange-500/30 text-orange-200' : 'bg-black/40 text-foreground/80'
                }`}>
                  {pair ? `${pair.number}${pair.side}` : ei + 1}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground leading-tight truncate">{ex.name}</p>
                  <div className="flex items-center gap-2 flex-wrap mt-0.5 text-[10px] text-muted-foreground">
                    {ex.sets != null && (
                      <span className="inline-flex items-center gap-1">
                        <Repeat className="w-2.5 h-2.5" />
                        {ex.sets}x{ex.reps ?? ''}
                      </span>
                    )}
                    {ex.rest_seconds != null && (
                      <span className="inline-flex items-center gap-1">
                        <Timer className="w-2.5 h-2.5" /> {ex.rest_seconds}s
                      </span>
                    )}
                    {ex.duration_seconds != null && !ex.sets && (
                      <span className="inline-flex items-center gap-1">
                        <Clock className="w-2.5 h-2.5" /> {ex.duration_seconds}s
                      </span>
                    )}
                    {ex.tempo && (
                      <span className="inline-flex items-center gap-1 text-amber-200/70">
                        Tempo {ex.tempo}
                      </span>
                    )}
                  </div>
                  {ex.notes && (
                    <p className="text-[10px] text-muted-foreground italic mt-1 leading-snug">{ex.notes}</p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => onRemove(ei)}
                  data-testid={`draft-remove-${sectionKey}-${di}-${ei}`}
                  className="shrink-0 w-7 h-7 rounded-lg bg-red-500/15 hover:bg-red-500/30 border border-red-500/30 text-red-300 hover:text-red-200 flex items-center justify-center transition-colors"
                  aria-label="Retirer"
                  title="Retirer"
                >
                  <span className="text-xs font-black">✕</span>
                </button>
              </li>
            </React.Fragment>
          );
        })}
      </ul>
    </div>
  );
}

